import { useEffect, useMemo, useRef, useState } from "react";
import { useList, useUpdate, useTranslate } from "@refinedev/core";
import {
  Button,
  BuildingIcon,
  CheckIcon,
  Field,
  PlusIcon,
  SearchIcon,
  SelectField,
  StatusBadge,
  SwapIcon,
  ToggleField,
  TrashIcon,
  type SelectOption,
} from "@fawkes/ui";

import { useModuleAvailability } from "../../api/modules";
import { getAccessToken } from "../../auth";
import { restoreSession } from "../../authProvider";
import "./VestigingenBeheer.css";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

/**
 * Een verzoek met het bearer-token, dat bij een 401 de sessie herstelt en het nog één keer probeert.
 *
 * <p>Waarom dit niet zonder kan: de logobytes gaan buiten de dataprovider om — het is een bestand,
 * geen json — en die provider is juist degene die het token ververst. Een kale fetch blijft dus het
 * token gebruiken dat toevallig in het geheugen stond, en na een herstart van de api is dat er een
 * die niet meer geldt. Het scherm werkt dan gewoon, maar deze ene tegel toont geen logo meer en
 * weigert op te slaan — precies zoals op 2026-09-24 gebeurde.
 */
async function metToken(url: string, init: RequestInit = {}): Promise<Response> {
  const kop = (bearer: string | null | undefined) => {
    const h = new Headers(init.headers);
    if (bearer) h.set("Authorization", `Bearer ${bearer}`);
    return h;
  };
  const res = await fetch(url, { ...init, headers: kop(getAccessToken()) });
  if (res.status !== 401) return res;
  if (await restoreSession()) {
    return fetch(url, { ...init, headers: kop(getAccessToken()) });
  }
  return res;
}

// vestigingen-beheer: het beheerscherm voor vestigingen. Links de lijst met vestigingen, rechts de
// gegevens van de gekozen vestiging in kaarten per onderwerp.
//
// Waarom kaarten en geen doorlopend formulier: de vestiging heeft zes groepen gegevens die niets met
// elkaar te maken hebben (basis, contact, bedrijf, adres, postadres, groep). Onder elkaar in één
// kolom moet je scrollen om te zien of het BTW-nummer al ingevuld is; naast elkaar in kaarten zie je
// alle zes in één blik en zoek je op onderwerp in plaats van op regelnummer.
//
// AANMAKEN KAN NIET, en dat zit in het schema: vestiging.klant_id is NOT NULL + UNIQUE + immutable —
// elke vestiging hoort 1-op-1 bij zijn eigen ASIEL-klant. Een vestiging maken vraagt dus eerst om die
// klant, en dat deed het Jmix-scherm ook niet (zijn aanmaakpad liep tegen dezelfde constraint aan).
// De knop staat er wel, uit, met de reden in de tooltip — dat is eerlijker dan hem weglaten en je
// laten zoeken waar hij is. Vestigingen gaan uit de roulatie via de schakelaar Actief.
// Schrijven gaat naar PUT /api/vestigingen-beheer/{id}.

const RESOURCE = "vestigingen-beheer";

// Basiswoordenlijst voor Land — gelijk aan VestigingenBeheerService.landen(). De landen die al op een
// vestiging staan worden erbij gezet, zodat een ingelezen waarde niet stilletjes verdwijnt.
const BASE_LANDEN = ["Nederland", "België", "Duitsland"];

interface Vestiging {
  id: number;
  code: string;
  naamKort: string;
  naam: string;
  actief: boolean;
  telefoonnummer: string | null;
  mobielnummer: string | null;
  noodnummer: string | null;
  emailAdres: string | null;
  website: string | null;
  kvkNummer: string | null;
  btwNummer: string | null;
  bankRekeningNummer: string | null;
  straat: string | null;
  huisnummer: string | null;
  postcode: string | null;
  woonplaats: string | null;
  land: string | null;
  postbusNummer: string | null;
  postbusPostcode: string | null;
  postbusWoonplaats: string | null;
  groep: string | null;
  ubn: string | null;
  defaultModule: string | null;
}

type StatusFilter = "all" | "active" | "inactive";

// Het formulier is een kopie van de gekozen vestiging met null → "" (anders raken de invoervelden
// hun controlled-status kwijt). Alleen de velden waar het formulier aan hangt.
type FormState = {
  code: string;
  naamKort: string;
  naam: string;
  actief: boolean;
  telefoonnummer: string;
  mobielnummer: string;
  noodnummer: string;
  emailAdres: string;
  website: string;
  kvkNummer: string;
  btwNummer: string;
  bankRekeningNummer: string;
  straat: string;
  huisnummer: string;
  postcode: string;
  woonplaats: string;
  land: string;
  postbusNummer: string;
  postbusPostcode: string;
  postbusWoonplaats: string;
  groep: string;
  ubn: string;
  defaultModule: string;
};

function toForm(v: Vestiging): FormState {
  return {
    code: v.code ?? "",
    naamKort: v.naamKort ?? "",
    naam: v.naam ?? "",
    actief: v.actief,
    telefoonnummer: v.telefoonnummer ?? "",
    mobielnummer: v.mobielnummer ?? "",
    noodnummer: v.noodnummer ?? "",
    emailAdres: v.emailAdres ?? "",
    website: v.website ?? "",
    kvkNummer: v.kvkNummer ?? "",
    btwNummer: v.btwNummer ?? "",
    bankRekeningNummer: v.bankRekeningNummer ?? "",
    straat: v.straat ?? "",
    huisnummer: v.huisnummer ?? "",
    postcode: v.postcode ?? "",
    woonplaats: v.woonplaats ?? "",
    land: v.land ?? "",
    postbusNummer: v.postbusNummer ?? "",
    postbusPostcode: v.postbusPostcode ?? "",
    postbusWoonplaats: v.postbusWoonplaats ?? "",
    groep: v.groep ?? "",
    ubn: v.ubn ?? "",
    defaultModule: v.defaultModule ?? "",
  };
}

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

export function VestigingenBeheer() {
  const t = useTranslate();
  const { data, isLoading, isError, refetch } = useList<Vestiging>({
    resource: RESOURCE,
    pagination: { mode: "off" },
  });
  const { mutate: update, isLoading: isSaving } = useUpdate();
  const { modules } = useModuleAvailability();

  const branches = useMemo(() => data?.data ?? [], [data]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Staat de groepkiezer open? Zolang groep één kolom is hoort er ook één groep in te staan; de
  // kiezer vervangt dus wat er staat in plaats van er iets bij te zetten.
  const [groepKiezen, setGroepKiezen] = useState(false);
  // Het logo staat los van het formulier: het gaat als bestand heen en weer en hoort niet mee te
  // reizen met elke toetsaanslag in de velden ernaast.
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoBezig, setLogoBezig] = useState(false);
  const [logoFout, setLogoFout] = useState(false);
  const [logoStand, setLogoStand] = useState(0);
  const bestandRef = useRef<HTMLInputElement | null>(null);

  const selected = branches.find((b) => b.id === selectedId) ?? null;

  const stuurLogo = async (bestand: File) => {
    if (selectedId == null) return;
    setLogoBezig(true);
    setLogoFout(false);
    const body = new FormData();
    body.append("file", bestand);
    try {
      const res = await metToken(`${API_URL}/vestigingen-beheer/${selectedId}/logo`, {
        method: "POST",
        body,
      });
      if (!res.ok) throw new Error(String(res.status));
      setLogoStand((n) => n + 1);
    } catch {
      setLogoFout(true);
    } finally {
      setLogoBezig(false);
      if (bestandRef.current) bestandRef.current.value = "";
    }
  };

  const wisLogo = async () => {
    if (selectedId == null) return;
    setLogoBezig(true);
    setLogoFout(false);
    try {
      const res = await metToken(`${API_URL}/vestigingen-beheer/${selectedId}/logo`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(String(res.status));
      setLogoStand((n) => n + 1);
    } catch {
      setLogoFout(true);
    } finally {
      setLogoBezig(false);
    }
  };

  // De bytes komen achter de bearer vandaan, dus niet als <img src>: ophalen en er een blob-url van
  // maken. Die url weer vrijgeven zodra er een andere vestiging gekozen wordt, anders lekt hij.
  useEffect(() => {
    if (selectedId == null) {
      setLogoUrl(null);
      return;
    }
    let actief = true;
    let url: string | null = null;
    metToken(`${API_URL}/vestigingen-beheer/${selectedId}/logo`)
      .then((res) => (res.ok ? res.blob() : null))
      .then((blob) => {
        if (!actief) return;
        url = blob ? URL.createObjectURL(blob) : null;
        setLogoUrl(url);
      })
      .catch(() => {
        if (actief) setLogoUrl(null);
      });
    return () => {
      actief = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [selectedId, logoStand]);

  // De eerste vestiging staat gekozen zodra de lijst er is; na verversen blijft de keuze staan.
  useEffect(() => {
    if (branches.length === 0) return;
    setSelectedId((current) =>
      current != null && branches.some((b) => b.id === current) ? current : branches[0].id,
    );
  }, [branches]);

  // De gekozen vestiging in het formulier (een andere keuze gooit niet-opgeslagen wijzigingen weg).
  useEffect(() => {
    if (selected) {
      setForm(toForm(selected));
      setDirty(false);
      setSaveError(null);
      setGroepKiezen(false);
    } else {
      setForm(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, branches]);

  const landen = useMemo(() => {
    const set = new Set(BASE_LANDEN);
    for (const b of branches) {
      if (b.land && b.land.trim()) set.add(b.land.trim());
    }
    return [...set];
  }, [branches]);

  // De groepen die al bestaan; een nieuwe naam mag je gewoon intypen.
  const groepen = useMemo(() => {
    const set = new Set<string>();
    for (const b of branches) {
      if (b.groep && b.groep.trim()) set.add(b.groep.trim());
    }
    return [...set].sort((a, b) => a.localeCompare(b, "nl"));
  }, [branches]);

  const filtered = useMemo(() => {
    const q = normalize(search);
    return branches.filter((b) => {
      if (statusFilter === "active" && !b.actief) return false;
      if (statusFilter === "inactive" && b.actief) return false;
      if (q === "") return true;
      return [b.naam, b.code, b.naamKort, b.woonplaats]
        .filter(Boolean)
        .some((s) => (s as string).toLowerCase().includes(q));
    });
  }, [branches, search, statusFilter]);

  const statusOptions: SelectOption[] = [
    { value: "all", label: t("vestigingenBeheer.filter.all") },
    { value: "active", label: t("vestigingenBeheer.filter.active") },
    { value: "inactive", label: t("vestigingenBeheer.filter.inactive") },
  ];
  const landOptions: SelectOption[] = [
    { value: "", label: "—" },
    ...landen.map((l) => ({ value: l, label: l })),
  ];
  // De modules komen uit dezelfde registratie die het menu gebruikt, zodat er hier nooit een module
  // in de lijst staat die de organisatie niet heeft.
  const moduleOptions: SelectOption[] = [
    { value: "", label: "—" },
    ...(modules ?? [])
      .filter((m) => m.enabled)
      .map((m) => ({ value: m.key, label: t(m.labelKey) })),
  ];

  function set<K extends keyof FormState>(field: K, value: FormState[K]) {
    setForm((f) => (f ? { ...f, [field]: value } : f));
    setDirty(true);
    setSaveError(null);
  }

  function cancel() {
    if (selected) setForm(toForm(selected));
    setDirty(false);
    setSaveError(null);
    setGroepKiezen(false);
  }

  // Postcode en woonplaats van het bezoekadres overnemen op het postadres.
  function copyVisitAddress() {
    setForm((f) => (f ? { ...f, postbusPostcode: f.postcode, postbusWoonplaats: f.woonplaats } : f));
    setDirty(true);
  }

  function save() {
    if (!selected || !form) return;
    update(
      // Spaties voor en achter tellen anders mee in de maximale lengte (een postcode "3585 LA " gaf een 400), en een
      // leeg veld gaat als null: een lege tekst valt bijvoorbeeld door de e-mailcontrole (Utrecht, 2026-10-09).
      { resource: RESOURCE, id: selected.id, values: Object.fromEntries(
        Object.entries(form).map(([k, w]) => [k, typeof w === "string" ? (w.trim() === "" ? null : w.trim()) : w])) as typeof form },
      {
        onSuccess: () => {
          setDirty(false);
          refetch();
        },
        onError: (err) => setSaveError(err?.message ?? t("vestigingenBeheer.saveFailed")),
      },
    );
  }

  const statusLabel = (actief: boolean) =>
    t(actief ? "vestigingenBeheer.status.active" : "vestigingenBeheer.status.inactive");

  return (
    <div className="vb-root">
      <div className="vb-layout">
        <section className="vb-master" aria-label={t("vestigingenBeheer.title")}>
          <div className="vb-master-head">
            <h2 className="vb-master-title">{t("vestigingenBeheer.title")}</h2>
            {/* Aanmaken kan niet zonder eerst een klant te maken (zie de kop van dit bestand). De
                knop staat er wel: hem weglaten laat je zoeken naar iets wat er niet is. */}
            <Button variant="primary" disabled title={t("vestigingenBeheer.new.disabled")}>
              <PlusIcon width={15} height={15} /> {t("vestigingenBeheer.new")}
            </Button>
          </div>

          <div className="vb-search">
            <SearchIcon className="vb-search__icon" width={16} height={16} />
            <input
              type="search"
              autoComplete="off"
              className="vb-search__input"
              placeholder={t("vestigingenBeheer.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={t("vestigingenBeheer.searchLabel")}
            />
          </div>

          {/* Het statusfilter staat niet in het ontwerp, maar zonder filter is een inactieve
              vestiging tussen eenendertig actieve alleen met de ogen te vinden. Compact gehouden. */}
          <div className="vb-master-filter">
            <label htmlFor="vb-status">{t("vestigingenBeheer.filterLabel")}</label>
            <select
              id="vb-status"
              className="fui-field__input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            >
              {statusOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {isLoading ? (
            <p className="fui-muted">{t("common.loading")}</p>
          ) : isError ? (
            <p className="fui-error">{t("vestigingenBeheer.loadFailed")}</p>
          ) : filtered.length === 0 ? (
            <p className="fui-muted">{t("vestigingenBeheer.empty")}</p>
          ) : (
            <ul className="vb-list">
              {filtered.map((b) => (
                <li key={b.id}>
                  <button
                    type="button"
                    className={`vb-item${b.id === selectedId ? " vb-item--active" : ""}`}
                    aria-current={b.id === selectedId}
                    onClick={() => setSelectedId(b.id)}
                  >
                    <span className="vb-item__text">
                      <span className="vb-item__naam">{b.naam || "—"}</span>
                      <span className="vb-item__sub">
                        {[b.woonplaats, b.naamKort || b.code].filter(Boolean).join(" • ") || "—"}
                      </span>
                    </span>
                    <StatusBadge tone={b.actief ? "success" : "neutral"}>
                      {statusLabel(b.actief)}
                    </StatusBadge>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="vb-detail">
          {selected == null || form == null ? (
            <p className="fui-muted">{t("vestigingenBeheer.detail.empty")}</p>
          ) : (
            <>
              <div className="vb-detail-head">
                <span className="vb-detail-icon" aria-hidden="true">
                  <BuildingIcon width={22} height={22} />
                </span>
                <div className="vb-detail-id">
                  <span className="vb-eyebrow">{t("vestigingenBeheer.eyebrow")}</span>
                  <h2 className="vb-detail-title">{form.naam || "—"}</h2>
                  <p className="vb-detail-meta">
                    {[form.woonplaats, form.naamKort || form.code].filter(Boolean).join(" • ")}
                    {" • "}
                    <span className={form.actief ? "vb-meta-actief" : "vb-meta-inactief"}>
                      {statusLabel(form.actief)}
                    </span>
                  </p>
                </div>
                <div className="vb-detail-actions">
                  <Button variant="secondary" onClick={cancel} disabled={!dirty || isSaving}>
                    {t("common.cancel")}
                  </Button>
                  <Button variant="primary" onClick={save} disabled={!dirty || isSaving}>
                    <CheckIcon width={15} height={15} /> {t("common.save")}
                  </Button>
                </div>
              </div>

              {saveError && <p className="fui-error">{saveError}</p>}

              <div className="vb-cards">
                <section className="vb-card">
                  <h3 className="vb-card-title">{t("vestigingenBeheer.group.basis")}</h3>
                  <div className="vb-grid">
                    <Field
                      label={t("vestigingenBeheer.field.code")}
                      value={form.code}
                      onChange={(e) => set("code", e.target.value)}
                      required
                    />
                    <Field
                      label={t("vestigingenBeheer.field.naamKort")}
                      value={form.naamKort}
                      onChange={(e) => set("naamKort", e.target.value)}
                      required
                    />
                    <div className="vb-col-2">
                      <Field
                        label={t("vestigingenBeheer.field.naam")}
                        value={form.naam}
                        onChange={(e) => set("naam", e.target.value)}
                        required
                      />
                    </div>
                    <SelectField
                      label={t("vestigingenBeheer.field.defaultModule")}
                      options={moduleOptions}
                      value={form.defaultModule}
                      onChange={(e) => set("defaultModule", e.target.value)}
                    />
                    <div className="vb-veld">
                      <span className="vb-veld__label">
                        {t("vestigingenBeheer.field.status")}
                      </span>
                      <ToggleField
                        label={statusLabel(form.actief)}
                        checked={form.actief}
                        onChange={(e) => set("actief", e.target.checked)}
                      />
                    </div>
                  </div>
                </section>

                <section className="vb-card">
                  <h3 className="vb-card-title">{t("vestigingenBeheer.group.contact")}</h3>
                  <div className="vb-grid">
                    <Field
                      label={t("vestigingenBeheer.field.telefoonnummer")}
                      value={form.telefoonnummer}
                      onChange={(e) => set("telefoonnummer", e.target.value)}
                    />
                    <Field
                      label={t("vestigingenBeheer.field.mobielnummer")}
                      value={form.mobielnummer}
                      onChange={(e) => set("mobielnummer", e.target.value)}
                    />
                    {/* In het rood, want dit is het nummer dat gebeld wordt als er iets mis is met een
                        dier in een pleeggezin. Tussen twintig andere velden is het anders niet terug te
                        vinden, en dat is precies het moment waarop je geen tijd hebt om te zoeken. */}
                    <div className="vb-col-2 vb-veld--nood">
                      <Field
                        label={t("vestigingenBeheer.field.noodnummer")}
                        value={form.noodnummer}
                        onChange={(e) => set("noodnummer", e.target.value)}
                        placeholder={t("vestigingenBeheer.field.noodnummer.placeholder")}
                      />
                    </div>
                    <div className="vb-col-2">
                      <Field
                        type="email"
                        invalidEmailMessage={t("validatie.emailadres")}
                        label={t("vestigingenBeheer.field.emailAdres")}
                        value={form.emailAdres}
                        onChange={(e) => set("emailAdres", e.target.value)}
                      />
                    </div>
                    <div className="vb-col-2">
                      <Field
                        label={t("vestigingenBeheer.field.website")}
                        value={form.website}
                        onChange={(e) => set("website", e.target.value)}
                      />
                    </div>
                  </div>
                </section>

                <section className="vb-card">
                  <h3 className="vb-card-title">{t("vestigingenBeheer.group.bedrijf")}</h3>
                  <div className="vb-grid">
                    <Field
                      label={t("vestigingenBeheer.field.kvkNummer")}
                      value={form.kvkNummer}
                      onChange={(e) => set("kvkNummer", e.target.value)}
                    />
                    <Field
                      label={t("vestigingenBeheer.field.btwNummer")}
                      value={form.btwNummer}
                      onChange={(e) => set("btwNummer", e.target.value)}
                    />
                    <div className="vb-col-2">
                      <Field
                        label={t("vestigingenBeheer.field.bankRekeningNummer")}
                        value={form.bankRekeningNummer}
                        onChange={(e) => set("bankRekeningNummer", e.target.value)}
                      />
                    </div>
                    <div className="vb-col-2">
                      <Field
                        label={t("vestigingenBeheer.field.ubn")}
                        value={form.ubn}
                        onChange={(e) => set("ubn", e.target.value)}
                      />
                    </div>
                  </div>
                </section>

                <section className="vb-card">
                  <h3 className="vb-card-title">{t("vestigingenBeheer.group.adres")}</h3>
                  <div className="vb-grid">
                    <Field
                      label={t("vestigingenBeheer.field.straat")}
                      value={form.straat}
                      onChange={(e) => set("straat", e.target.value)}
                    />
                    <Field
                      label={t("vestigingenBeheer.field.huisnummer")}
                      value={form.huisnummer}
                      onChange={(e) => set("huisnummer", e.target.value)}
                    />
                    <Field
                      label={t("vestigingenBeheer.field.postcode")}
                      value={form.postcode}
                      onChange={(e) => set("postcode", e.target.value)}
                    />
                    <Field
                      label={t("vestigingenBeheer.field.woonplaats")}
                      value={form.woonplaats}
                      onChange={(e) => set("woonplaats", e.target.value)}
                    />
                    <div className="vb-col-2">
                      <SelectField
                        label={t("vestigingenBeheer.field.land")}
                        options={landOptions}
                        value={form.land}
                        onChange={(e) => set("land", e.target.value)}
                      />
                    </div>
                  </div>
                </section>

                <section className="vb-card">
                  <div className="vb-card-head">
                    <h3 className="vb-card-title">{t("vestigingenBeheer.group.postadres")}</h3>
                    <button type="button" className="vb-card-action" onClick={copyVisitAddress}>
                      <SwapIcon width={14} height={14} />
                      {t("vestigingenBeheer.postadres.copy")}
                    </button>
                  </div>
                  <div className="vb-grid">
                    <Field
                      label={t("vestigingenBeheer.field.postbusNummer")}
                      value={form.postbusNummer}
                      onChange={(e) => set("postbusNummer", e.target.value)}
                    />
                    <Field
                      label={t("vestigingenBeheer.field.postbusPostcode")}
                      value={form.postbusPostcode}
                      onChange={(e) => set("postbusPostcode", e.target.value)}
                    />
                    <div className="vb-col-2">
                      <Field
                        label={t("vestigingenBeheer.field.postbusWoonplaats")}
                        value={form.postbusWoonplaats}
                        onChange={(e) => set("postbusWoonplaats", e.target.value)}
                      />
                    </div>
                  </div>
                  {!form.postbusNummer && !form.postbusPostcode && !form.postbusWoonplaats && (
                    <p className="vb-card-note">{t("vestigingenBeheer.postadres.empty")}</p>
                  )}
                </section>

                <section className="vb-card">
                  <div className="vb-card-head">
                    <h3 className="vb-card-title">{t("vestigingenBeheer.group.groep")}</h3>
                    {/* Eén groep per vestiging: het is één kolom. De knop koppelt er dus een groep
                        aan of vervangt de bestaande — vandaar dat hij van naam verandert. */}
                    <button
                      type="button"
                      className="vb-card-action"
                      onClick={() => setGroepKiezen((x) => !x)}
                    >
                      <PlusIcon width={14} height={14} />
                      {form.groep
                        ? t("vestigingenBeheer.groep.change")
                        : t("vestigingenBeheer.groep.add")}
                    </button>
                  </div>

                  {groepKiezen && (
                    <>
                      <Field
                        label={t("vestigingenBeheer.field.groep")}
                        list="vb-groepen"
                        value={form.groep}
                        onChange={(e) => set("groep", e.target.value)}
                        placeholder={t("vestigingenBeheer.field.groep.placeholder")}
                      />
                      <datalist id="vb-groepen">
                        {groepen.map((g) => (
                          <option key={g} value={g} />
                        ))}
                      </datalist>
                    </>
                  )}

                  {form.groep ? (
                    <div className="vb-groep">
                      <span className="vb-groep__text">
                        <span className="vb-groep__naam">{form.groep}</span>
                        <span className="vb-groep__sub">{t("vestigingenBeheer.groep.linked")}</span>
                      </span>
                      <button
                        type="button"
                        className="vb-groep__weg"
                        title={t("vestigingenBeheer.groep.remove")}
                        aria-label={t("vestigingenBeheer.groep.remove")}
                        onClick={() => set("groep", "")}
                      >
                        <TrashIcon width={15} height={15} />
                      </button>
                    </div>
                  ) : (
                    !groepKiezen && (
                      <p className="vb-card-note">{t("vestigingenBeheer.groep.empty")}</p>
                    )
                  )}
                </section>

                {/* Het beeldmerk van dit asiel; het komt terug op zijn eigen pagina op DierMatch. */}
                <section className="vb-card">
                  <div className="vb-card-head">
                    <h3 className="vb-card-title">{t("vestigingenBeheer.group.logo")}</h3>
                    {logoUrl && (
                      <button
                        type="button"
                        className="vb-card-action"
                        disabled={logoBezig}
                        onClick={() => void wisLogo()}
                      >
                        <TrashIcon width={14} height={14} />
                        {t("vestigingenBeheer.logo.remove")}
                      </button>
                    )}
                  </div>

                  <div className="vb-logo">
                    {logoUrl ? (
                      <img className="vb-logo__beeld" src={logoUrl} alt="" />
                    ) : (
                      <span className="vb-logo__leeg" aria-hidden="true">
                        <BuildingIcon width={26} height={26} />
                      </span>
                    )}
                    <div className="vb-logo__zij">
                      <button
                        type="button"
                        className="vb-logo__kies"
                        disabled={logoBezig}
                        onClick={() => bestandRef.current?.click()}
                      >
                        {logoBezig
                          ? t("vestigingenBeheer.logo.busy")
                          : logoUrl
                            ? t("vestigingenBeheer.logo.replace")
                            : t("vestigingenBeheer.logo.choose")}
                      </button>
                      <p className="vb-card-note">{t("vestigingenBeheer.logo.hint")}</p>
                      {logoFout && <p className="vb-logo__fout">{t("vestigingenBeheer.logo.failed")}</p>}
                    </div>
                  </div>
                  <input
                    ref={bestandRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    hidden
                    onChange={(e) => {
                      const bestand = e.target.files?.[0];
                      if (bestand) void stuurLogo(bestand);
                    }}
                  />
                </section>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
